import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertTriangle, Shield, Info, AlertCircle, Search,
  RefreshCw, Download, Eye, Monitor, Terminal, FileText,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PageHead, StatTile, Section } from "@/components/dash/primitives";

interface UnifiedLog {
  id: string;
  hostname: string;
  message: string;
  event_time: string;
  source_os: "windows" | "linux";
  event_id?: number;
  log_name?: string;
  level_name?: string;
  provider_name?: string;
  user_sid?: string;
  raw?: any;
  severity?: string;
  log_source?: string;
  process_name?: string;
  user_name?: string;
  unit?: string;
}

const CRITICAL_EVENTS: Record<number, string> = {
  4625: "Failed Logon", 4740: "Account Locked Out", 4720: "Account Created",
  4726: "Account Deleted", 4738: "Account Changed", 4648: "Explicit Credentials Logon",
  4688: "Process Created", 4104: "PowerShell Script Block",
  7031: "Service Terminated Unexpectedly", 7034: "Service Crashed",
  1074: "System Shutdown/Restart", 4624: "Successful Logon",
  4634: "Account Logged Off", 5156: "Network Connection Allowed",
  5157: "Network Connection Blocked",
};

function mapSeverityToLevel(severity: string): string {
  const s = severity?.toLowerCase();
  if (["crit", "emerg", "alert"].includes(s)) return "Critical";
  if (["err", "error"].includes(s)) return "Error";
  if (["warning", "warn"].includes(s)) return "Warning";
  return "Information";
}

function levelPillClass(level: string): string {
  const l = level.toLowerCase();
  if (l === "critical") return "log-level-pill log-level-pill--critical";
  if (l === "error") return "log-level-pill log-level-pill--error";
  if (l === "warning") return "log-level-pill log-level-pill--warning";
  return "log-level-pill log-level-pill--info";
}

function levelIcon(level: string) {
  const l = level.toLowerCase();
  if (l === "critical") return <AlertTriangle size={12} />;
  if (l === "error") return <AlertCircle size={12} />;
  if (l === "warning") return <AlertTriangle size={12} />;
  return <Info size={12} />;
}

export default function LogsPage() {
  const { profile } = useAuth();
  const [logs, setLogs] = useState<UnifiedLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedHostname, setSelectedHostname] = useState("all");
  const [selectedSource, setSelectedSource] = useState<string>("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [allHostnames, setAllHostnames] = useState<string[]>([]);
  const [totalCounts, setTotalCounts] = useState({
    windows: 0,
    linux: 0,
    critical: 0,
  });

  // Page size for the in-memory log viewer. Backed by batched fetching so the
  // counts and the rendered list both go past Supabase's default 1000-row cap.
  const PAGE_SIZE = 1000;
  const MAX_LOGS = 5000;

  const fetchHostnames = useCallback(async () => {
    if (!profile?.tenant_id) return;
    const [{ data: wh }, { data: lh }] = await Promise.all([
      supabase.from("windows_event_logs").select("hostname").eq("tenant_id", profile.tenant_id).limit(1000),
      supabase.from("linux_logs").select("hostname").eq("tenant_id", profile.tenant_id).limit(1000),
    ]);
    const set = new Set<string>();
    wh?.forEach((r: any) => set.add(r.hostname));
    lh?.forEach((r: any) => set.add(r.hostname));
    setAllHostnames([...set].sort());
  }, [profile?.tenant_id]);

  const fetchLogs = useCallback(async () => {
    if (!profile?.tenant_id) return;
    setLoading(true);
    try {
      const fetchWindows = selectedSource === "all" || selectedSource === "windows";
      const fetchLinux = selectedSource === "all" || selectedSource === "linux";

      const linuxSeverityMap: Record<string, string[]> = {
        Critical: ["crit", "emerg", "alert"],
        Error: ["err", "error"],
        Warning: ["warning", "warn"],
        Information: ["info", "notice", "debug"],
      };

      // Build filterable base queries (used by both count + paged fetch)
      const buildWindowsQuery = (forCount: boolean) => {
        let q = forCount
          ? supabase
              .from("windows_event_logs")
              .select("id", { count: "exact", head: true })
              .eq("tenant_id", profile.tenant_id)
          : supabase
              .from("windows_event_logs")
              .select(
                "id, hostname, event_id, log_name, level_name, provider_name, message, user_sid, event_time, raw"
              )
              .eq("tenant_id", profile.tenant_id);
        if (selectedHostname !== "all") q = q.eq("hostname", selectedHostname);
        if (selectedLevel !== "all") q = q.eq("level_name", selectedLevel);
        if (searchTerm) {
          q = q.or(
            `message.ilike.%${searchTerm}%,provider_name.ilike.%${searchTerm}%,hostname.ilike.%${searchTerm}%`
          );
        }
        return q;
      };

      const buildLinuxQuery = (forCount: boolean) => {
        let q = forCount
          ? supabase
              .from("linux_logs")
              .select("id", { count: "exact", head: true })
              .eq("tenant_id", profile.tenant_id)
          : supabase
              .from("linux_logs")
              .select(
                "id, hostname, message, event_time, severity, log_source, process_name, user_name, unit, raw"
              )
              .eq("tenant_id", profile.tenant_id);
        if (selectedHostname !== "all") q = q.eq("hostname", selectedHostname);
        if (selectedLevel !== "all") {
          const sevs = linuxSeverityMap[selectedLevel];
          if (sevs) q = q.in("severity", sevs);
        }
        if (searchTerm) {
          q = q.or(
            `message.ilike.%${searchTerm}%,process_name.ilike.%${searchTerm}%,hostname.ilike.%${searchTerm}%`
          );
        }
        return q;
      };

      // Pull *real* totals via count queries (head:true → no rows transferred,
      // not subject to the 1000-row default).
      const [
        winCountRes,
        linCountRes,
        winCritRes,
        linCritRes,
      ] = await Promise.all([
        fetchWindows
          ? buildWindowsQuery(true)
          : Promise.resolve({ count: 0 } as any),
        fetchLinux
          ? buildLinuxQuery(true)
          : Promise.resolve({ count: 0 } as any),
        fetchWindows
          ? supabase
              .from("windows_event_logs")
              .select("id", { count: "exact", head: true })
              .eq("tenant_id", profile.tenant_id)
              .in("level_name", ["Critical", "Error"])
          : Promise.resolve({ count: 0 } as any),
        fetchLinux
          ? supabase
              .from("linux_logs")
              .select("id", { count: "exact", head: true })
              .eq("tenant_id", profile.tenant_id)
              .in("severity", ["crit", "emerg", "alert", "err", "error"])
          : Promise.resolve({ count: 0 } as any),
      ]);

      setTotalCounts({
        windows: (winCountRes as any).count || 0,
        linux: (linCountRes as any).count || 0,
        critical:
          ((winCritRes as any).count || 0) + ((linCritRes as any).count || 0),
      });

      // Paged fetch in batches of PAGE_SIZE so we can show > 1000 rows
      // without blowing the Supabase row limit on a single request.
      const fetchPaged = async <T,>(
        builder: () => any,
        max: number
      ): Promise<T[]> => {
        const out: T[] = [];
        for (let from = 0; from < max; from += PAGE_SIZE) {
          const to = Math.min(from + PAGE_SIZE - 1, max - 1);
          const { data, error } = await builder()
            .order("event_time", { ascending: false })
            .range(from, to);
          if (error) throw error;
          const rows = (data || []) as T[];
          out.push(...rows);
          if (rows.length < PAGE_SIZE) break;
        }
        return out;
      };

      const results: UnifiedLog[] = [];

      if (fetchWindows) {
        const winRows = await fetchPaged<any>(
          () => buildWindowsQuery(false),
          MAX_LOGS
        );
        winRows.forEach((r: any) =>
          results.push({
            id: `w-${r.id}`,
            hostname: r.hostname,
            message: r.message,
            event_time: r.event_time,
            source_os: "windows",
            event_id: r.event_id,
            log_name: r.log_name,
            level_name: r.level_name,
            provider_name: r.provider_name,
            user_sid: r.user_sid,
            raw: r.raw,
          })
        );
      }

      if (fetchLinux) {
        const linRows = await fetchPaged<any>(
          () => buildLinuxQuery(false),
          MAX_LOGS
        );
        linRows.forEach((r: any) =>
          results.push({
            id: `l-${r.id}`,
            hostname: r.hostname,
            message: r.message,
            event_time: r.event_time,
            source_os: "linux",
            severity: r.severity,
            log_source: r.log_source,
            process_name: r.process_name,
            user_name: r.user_name,
            unit: r.unit,
            raw: r.raw,
            level_name: mapSeverityToLevel(r.severity),
          })
        );
      }

      results.sort(
        (a, b) =>
          new Date(b.event_time).getTime() - new Date(a.event_time).getTime()
      );
      setLogs(results.slice(0, MAX_LOGS));
    } catch {
      toast.error("Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  }, [profile?.tenant_id, selectedSource, selectedHostname, selectedLevel, searchTerm]);

  useEffect(() => { fetchHostnames(); }, [fetchHostnames]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  useEffect(() => {
    if (!profile?.tenant_id) return;
    const ch1 = supabase.channel("logs-win").on("postgres_changes", {
      event: "INSERT", schema: "public", table: "windows_event_logs",
      filter: `tenant_id=eq.${profile.tenant_id}`,
    }, () => { if (autoRefresh) fetchLogs(); }).subscribe();
    const ch2 = supabase.channel("logs-lin").on("postgres_changes", {
      event: "INSERT", schema: "public", table: "linux_logs",
      filter: `tenant_id=eq.${profile.tenant_id}`,
    }, () => { if (autoRefresh) fetchLogs(); }).subscribe();
    return () => { supabase.removeChannel(ch1); supabase.removeChannel(ch2); };
  }, [profile?.tenant_id, autoRefresh, fetchLogs]);

  const getDisplayLevel = (log: UnifiedLog) => log.level_name || "Information";
  const getEventDescription = (eventId: number) => CRITICAL_EVENTS[eventId] || `Event ${eventId}`;
  const formatLogTime = (ts: string) => new Date(ts).toLocaleString();

  const exportLogs = () => {
    const csv = [
      ["Time", "OS", "Hostname", "Level", "Source", "Message"].join(","),
      ...logs.map((log) => [
        log.event_time, log.source_os, log.hostname,
        getDisplayLevel(log),
        log.source_os === "windows" ? log.provider_name : log.process_name,
        `"${log.message.replace(/"/g, '""')}"`,
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Logs exported");
  };

  const stats = useMemo(() => {
    return {
      total: totalCounts.windows + totalCounts.linux,
      windowsCount: totalCounts.windows,
      linuxCount: totalCounts.linux,
      criticalCount: totalCounts.critical,
    };
  }, [totalCounts]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <PageHead
        title="Logs"
        subtitle="Unified log viewer for Windows & Linux servers"
        right={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Auto {autoRefresh ? "ON" : "OFF"}
            </Button>
            <Button variant="outline" size="sm" onClick={exportLogs}>
              <Download className="h-4 w-4 mr-2" />Export
            </Button>
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
          </>
        }
      />

      <div className="stat-grid">
        <StatTile
          label="Total Logs"
          value={stats.total.toLocaleString()}
          sub="Across all hosts"
          icon={<FileText size={16} />}
        />
        <StatTile
          label="Windows"
          value={stats.windowsCount.toLocaleString()}
          sub="Windows event logs"
          icon={<Monitor size={16} />}
        />
        <StatTile
          label="Linux"
          value={stats.linuxCount.toLocaleString()}
          sub="Linux system logs"
          icon={<Terminal size={16} />}
        />
        <StatTile
          label="Critical / Errors"
          value={stats.criticalCount.toLocaleString()}
          sub="High-severity events"
          tone={stats.criticalCount > 0 ? "bad" : "good"}
          icon={<AlertTriangle size={16} />}
        />
      </div>

      <Section title="Filters" subtitle="Narrow down log entries">
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--fg-3)",
              }}
            />
            <Input
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger><SelectValue placeholder="OS Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="windows">Windows</SelectItem>
              <SelectItem value="linux">Linux</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedLevel} onValueChange={setSelectedLevel}>
            <SelectTrigger><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="Critical">Critical</SelectItem>
              <SelectItem value="Error">Error</SelectItem>
              <SelectItem value="Warning">Warning</SelectItem>
              <SelectItem value="Information">Information</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedHostname} onValueChange={setSelectedHostname}>
            <SelectTrigger><SelectValue placeholder="Server" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Servers</SelectItem>
              {allHostnames.map((h) => (
                <SelectItem key={h} value={h}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section
        title="Log Entries"
        subtitle={`Showing ${logs.length.toLocaleString()} of ${stats.total.toLocaleString()} entries · realtime`}
      >
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <RefreshCw className="animate-spin" size={28} />
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--fg-3)" }}>
            <Shield size={36} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No logs found</p>
            <p style={{ fontSize: 13 }}>Try adjusting filters or wait for new entries.</p>
          </div>
        ) : (
          <ScrollArea className="h-[600px]">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {logs.map((log) => {
                const level = getDisplayLevel(log);
                return (
                  <div
                    key={log.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: 12,
                      border: "1px solid var(--line)",
                      borderRadius: 10,
                      background: "var(--bg-1)",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexWrap: "wrap",
                          marginBottom: 6,
                        }}
                      >
                        <span className={levelPillClass(level)}>
                          {levelIcon(level)}
                          {level}
                        </span>
                        <span className="pill pill--default pill--mono">
                          {log.source_os === "windows" ? (
                            <><Monitor size={10} style={{ marginRight: 4 }} />WIN</>
                          ) : (
                            <><Terminal size={10} style={{ marginRight: 4 }} />LIN</>
                          )}
                        </span>
                        <span
                          style={{
                            fontFamily: "'Geist Mono', monospace",
                            fontSize: 12,
                            color: "var(--fg-2)",
                          }}
                        >
                          {formatLogTime(log.event_time)}
                        </span>
                        <span
                          style={{
                            fontFamily: "'Geist Mono', monospace",
                            fontSize: 12,
                            color: "var(--fg-3)",
                          }}
                        >
                          · {log.hostname}
                        </span>
                        {log.event_id && (
                          <span className="pill pill--info pill--mono">
                            {log.event_id} · {getEventDescription(log.event_id)}
                          </span>
                        )}
                      </div>
                      <p
                        style={{
                          fontSize: 13,
                          color: "var(--fg-2)",
                          marginBottom: 4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {log.message}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          fontSize: 11,
                          color: "var(--fg-3)",
                          fontFamily: "'Geist Mono', monospace",
                        }}
                      >
                        <span>
                          src: {log.source_os === "windows" ? log.provider_name : log.process_name || log.log_source}
                        </span>
                        {(log.user_sid || log.user_name) && (
                          <span>· user: {log.user_sid || log.user_name}</span>
                        )}
                        {log.unit && <span>· unit: {log.unit}</span>}
                      </div>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>Log Details</DialogTitle>
                          <DialogDescription>
                            {log.source_os === "windows" && log.event_id
                              ? `Event ID ${log.event_id} - ${getEventDescription(log.event_id)}`
                              : `${log.source_os.toUpperCase()} - ${log.log_source || log.process_name || "System"}`}
                          </DialogDescription>
                        </DialogHeader>
                        <ScrollArea className="max-h-[60vh]">
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium">Time</label>
                                <p className="text-sm text-muted-foreground">{formatLogTime(log.event_time)}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Hostname</label>
                                <p className="text-sm text-muted-foreground">{log.hostname}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">OS</label>
                                <p className="text-sm text-muted-foreground">{log.source_os === "windows" ? "Windows" : "Linux"}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Level</label>
                                <p className="text-sm text-muted-foreground">{level}</p>
                              </div>
                              {log.event_id && (
                                <div>
                                  <label className="text-sm font-medium">Event ID</label>
                                  <p className="text-sm text-muted-foreground">{log.event_id}</p>
                                </div>
                              )}
                              <div>
                                <label className="text-sm font-medium">Source</label>
                                <p className="text-sm text-muted-foreground">
                                  {log.source_os === "windows" ? log.provider_name : log.process_name || log.log_source}
                                </p>
                              </div>
                              {(log.user_sid || log.user_name) && (
                                <div>
                                  <label className="text-sm font-medium">User</label>
                                  <p className="text-sm text-muted-foreground">{log.user_sid || log.user_name}</p>
                                </div>
                              )}
                              {log.log_name && (
                                <div>
                                  <label className="text-sm font-medium">Log Name</label>
                                  <p className="text-sm text-muted-foreground">{log.log_name}</p>
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="text-sm font-medium">Message</label>
                              <Textarea value={log.message} readOnly className="mt-1" rows={4} />
                            </div>
                            {log.raw && (
                              <div>
                                <label className="text-sm font-medium">Raw Data</label>
                                <Textarea
                                  value={JSON.stringify(log.raw, null, 2)}
                                  readOnly
                                  className="mt-1 font-mono text-xs"
                                  rows={6}
                                />
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </Section>
    </div>
  );
}
